import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LoadingContainer, ButtonSpinner } from './ui/Spinner';
import { PlusIcon } from '@heroicons/react/24/outline';
import PlayerTypeIcon, {
    VS_AGGRESSION_OPTIONS,
    AGGRESSION_STYLE_OPTIONS,
    PREFLOP_HANDS_OPTIONS,
    PREFLOP_AGGRESSION_OPTIONS,
    MatrixBadge
} from './PlayerTypeIcon';

// Reusable Input Component
const Input = ({ label, type = "text", value, onChange, required = false, className = "", ...props }) => (
    <div className={`relative ${className}`}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600
                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white
                     focus:border-primary-500 dark:focus:border-primary-400
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400
                     disabled:opacity-50 disabled:cursor-not-allowed"
            {...props}
        />
    </div>
);

// Reusable Select Component
const Select = ({ label, name, value, onChange, options, className = "" }) => (
    <div className={`relative ${className}`}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
        </label>
        <select
            name={name}
            value={value}
            onChange={onChange}
            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600
                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white
                     focus:border-primary-500 dark:focus:border-primary-400
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
        >
            {Object.entries(options).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
            ))}
        </select>
    </div>
);

function PlayersPage() {
    const navigate = useNavigate();
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [form, setForm] = useState({
        name: '',
        notes: '',
        // New matrix fields
        preflop_hands: 'unknown',
        preflop_aggression: 'unknown',
        vs_aggression: 'unknown',
        aggression_style: 'unknown',
    });
    const [profilePicture, setProfilePicture] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(null);
    const [showImageModal, setShowImageModal] = useState(null);
    const [tendencyTags, setTendencyTags] = useState([]);

    // Fetch tendency tags
    const fetchTendencyTags = async () => {
        try {
            const response = await axios.get('/api/tendency-tags/');
            setTendencyTags(response.data);
        } catch (err) {
            console.error('Failed to fetch tendency tags:', err);
        }
    };

    // Get tendency label from key
    const getTendencyLabel = (key) => {
        const tag = tendencyTags.find(t => t.key === key);
        return tag ? tag.name : key.replace(/_/g, ' ');
    };

    const fetchPlayers = (search = '') => {
        setLoading(true);
        const params = search ? { search } : {};
        axios.get('/api/players/', { params })
            .then(res => {
                const playerData = res.data?.results || res.data || [];
                setPlayers(playerData);
                setLoading(false);
            })
            .catch(err => {
                setError(err.response?.data?.detail || 'Failed to fetch players.');
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchPlayers();
        fetchTendencyTags();
    }, []);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchQuery !== '') {
                fetchPlayers(searchQuery);
            } else {
                fetchPlayers();
            }
        }, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleInputChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setProfilePicture(file);
        if (file) {
            setPreviewUrl(URL.createObjectURL(file));
        } else {
            setPreviewUrl(null);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormLoading(true);
        setFormError(null);
        setFormSuccess(null);

        const formData = new FormData();
        formData.append('name', form.name);
        formData.append('notes', form.notes || '');
        formData.append('preflop_hands', form.preflop_hands);
        formData.append('preflop_aggression', form.preflop_aggression);
        formData.append('vs_aggression', form.vs_aggression);
        formData.append('aggression_style', form.aggression_style);

        if (profilePicture) {
            formData.append('profile_picture', profilePicture);
        }

        axios.post('/api/players/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(res => {
                setForm({
                    name: '',
                    notes: '',
                    preflop_hands: 'unknown',
                    preflop_aggression: 'unknown',
                    vs_aggression: 'unknown',
                    aggression_style: 'unknown',
                });
                setProfilePicture(null);
                setPreviewUrl(null);
                setFormSuccess('Player created!');
                fetchPlayers();
            })
            .catch(err => {
                setFormError(err.response?.data?.detail || 'Failed to create player.');
            })
            .finally(() => setFormLoading(false));
    };

    return (
        <div className="flex-grow px-4 py-4 mx-auto w-full max-w-4xl sm:max-w-6xl">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">
                Players
            </h1>

            {/* Add New Player Form */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200
                          dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Add New Player
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Row 1: Name and Notes */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <Input
                            label="Name"
                            name="name"
                            value={form.name}
                            onChange={handleInputChange}
                            required
                            className="flex-1"
                        />
                        <Input
                            label="Notes"
                            name="notes"
                            value={form.notes}
                            onChange={handleInputChange}
                            className="flex-1"
                            placeholder="Optional notes..."
                        />
                    </div>

                    {/* Row 2: Preflop Framework */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Preflop Profile
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Select
                                label="Hand Selection"
                                name="preflop_hands"
                                value={form.preflop_hands}
                                onChange={handleInputChange}
                                options={PREFLOP_HANDS_OPTIONS}
                                className="flex-1"
                            />
                            <Select
                                label="Aggression"
                                name="preflop_aggression"
                                value={form.preflop_aggression}
                                onChange={handleInputChange}
                                options={PREFLOP_AGGRESSION_OPTIONS}
                                className="flex-1"
                            />
                        </div>
                    </div>

                    {/* Row 3: Postflop Matrix */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Postflop Profile
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Select
                                label="vs Aggression"
                                name="vs_aggression"
                                value={form.vs_aggression}
                                onChange={handleInputChange}
                                options={VS_AGGRESSION_OPTIONS}
                                className="flex-1"
                            />
                            <Select
                                label="Aggression Style"
                                name="aggression_style"
                                value={form.aggression_style}
                                onChange={handleInputChange}
                                options={AGGRESSION_STYLE_OPTIONS}
                                className="flex-1"
                            />
                        </div>
                    </div>

                    {/* Row 4: Picture and Submit */}
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end pt-2">
                        <label className="relative h-10 px-4 flex items-center gap-2
                                      border border-primary-500 text-primary-600
                                      dark:text-primary-400 rounded-lg cursor-pointer
                                      hover:bg-primary-50 dark:hover:bg-primary-900/10
                                      transition-colors">
                            <span className="font-medium text-sm">
                                {profilePicture ? 'Change Picture' : 'Upload Picture'}
                            </span>
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </label>
                        {previewUrl && (
                            <div className="w-10 h-10 rounded-full overflow-hidden">
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}
                        <div className="flex-1"></div>
                        <button
                            type="submit"
                            disabled={formLoading}
                            className="h-10 px-6 bg-primary-600 text-white rounded-lg
                                     hover:bg-primary-700 focus:outline-none focus:ring-2
                                     focus:ring-primary-500 focus:ring-offset-2
                                     font-medium transition-colors disabled:opacity-50
                                     disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {formLoading ? (
                                <ButtonSpinner />
                            ) : (
                                <>
                                    <PlusIcon className="h-5 w-5" />
                                    <span>Add Player</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {formError && (
                    <div className="mt-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30
                                  text-red-700 dark:text-red-400 border border-red-200
                                  dark:border-red-800">
                        {formError}
                    </div>
                )}

                {formSuccess && (
                    <div className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-900/30
                                  text-green-700 dark:text-green-400 border border-green-200
                                  dark:border-green-800">
                        {formSuccess}
                    </div>
                )}
            </div>

            {/* Player List */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200
                          dark:border-gray-700 rounded-lg p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Player List
                    </h2>
                    <div className="w-full sm:w-64">
                        <Input
                            label="Search Players"
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by name..."
                            className="w-full"
                        />
                    </div>
                </div>

                {loading ? (
                    <LoadingContainer />
                ) : error ? (
                    <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30
                                  text-red-700 dark:text-red-400 border border-red-200
                                  dark:border-red-800">
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                            {players.length} player{players.length !== 1 ? 's' : ''} found
                            {searchQuery && ` matching "${searchQuery}"`}
                        </div>
                        {players.length === 0 ? (
                            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                                {searchQuery ? `No players found matching "${searchQuery}"` : 'No players found. Add your first player to get started.'}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {players.map(player => (
                                    <div
                                        key={player.id}
                                        onClick={() => navigate(`/players/${player.id}`)}
                                        className="p-4 rounded-lg border border-gray-200
                                                 dark:border-gray-700 bg-gray-50
                                                 dark:bg-gray-800/50 cursor-pointer
                                                 hover:bg-gray-100 dark:hover:bg-gray-700
                                                 transition-colors"
                                    >
                                        <div className="flex items-start gap-3 mb-3">
                                            {/* Avatar */}
                                            <div className="w-12 h-12 rounded-full flex items-center justify-center
                                                          text-xl font-bold bg-primary-600 text-white flex-shrink-0">
                                                {(player.profile_picture_url || player.profile_picture) ? (
                                                    <img
                                                        src={player.profile_picture_url || player.profile_picture}
                                                        alt={player.name}
                                                        className="w-full h-full rounded-full object-cover"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowImageModal(player);
                                                        }}
                                                    />
                                                ) : (
                                                    player.name?.[0] || '?'
                                                )}
                                            </div>
                                            {/* Name and notes */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <PlayerTypeIcon playerType={player.player_category} size="w-3 h-3" />
                                                    <span className="font-semibold text-gray-900 dark:text-white truncate">
                                                        {player.name}
                                                    </span>
                                                </div>
                                                {player.notes && (
                                                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                                        {player.notes}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Matrix badges */}
                                        <div className="space-y-2">
                                            <div className="flex flex-wrap gap-1">
                                                <MatrixBadge value={player.preflop_hands} options={PREFLOP_HANDS_OPTIONS} />
                                                <MatrixBadge value={player.preflop_aggression} options={PREFLOP_AGGRESSION_OPTIONS} />
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                <MatrixBadge value={player.vs_aggression} options={VS_AGGRESSION_OPTIONS} />
                                                <MatrixBadge value={player.aggression_style} options={AGGRESSION_STYLE_OPTIONS} />
                                            </div>
                                        </div>

                                        {/* Confirmed tendencies */}
                                        {player.confirmed_tendencies && Object.keys(player.confirmed_tendencies).length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                                    Confirmed Tendencies:
                                                </div>
                                                <div className="flex flex-wrap gap-1">
                                                    {Object.entries(player.confirmed_tendencies).map(([key, data]) => (
                                                        <span
                                                            key={key}
                                                            className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30
                                                                     text-orange-700 dark:text-orange-400
                                                                     text-xs rounded-full"
                                                        >
                                                            {getTendencyLabel(key)} ({data.count}x)
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Image Modal */}
            {showImageModal && (showImageModal.profile_picture_url || showImageModal.profile_picture) && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto"
                     onClick={() => setShowImageModal(null)}>
                    <div className="relative max-w-4xl max-h-full">
                        <img
                            src={showImageModal.profile_picture_url || showImageModal.profile_picture}
                            alt={showImageModal.name}
                            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                        />
                        <div className="mt-4 text-center">
                            <p className="text-white text-lg font-medium">{showImageModal.name}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PlayersPage;
